import React from "react";
import { PulseLoader } from "react-spinners";
import { useQuery } from "@tanstack/react-query";
import { ErrorBoundary } from "react-error-boundary";

import Form from "./containers/form.jsx";
import Alert from "./components/alert.jsx";
import ErrorFallback from "./components/error-fallback.jsx";
import "./index.css";

const logError = (error) => {
  // @ts-ignore if BugSnag isnt on the window we report to console instead
  console.error(
    `There was an error in the quiz renderer application`,
    error?.message ?? error,
  );
};

/**
 * This is the main entry point for the Form Renderer.
 * @typedef {object} AppProps
 * @property {string} formId - The ID of the form to render.
 * @property {string} apiKey - The API key to use when fetching the form.
 * @property {string} respondentId - The ID of the respondent to use when submitting the form.
 * @property {object} passThroughData - Any data to pass through to the form.
 * @property {boolean} flattenPages - Whether to flatten the pages of the form.
 */

/**
 * @property {AppProps} props - props
 * @returns {React.ReactElement} - The form
 */
function App() {
  const formId = window.formRendererData?.formId;
  const respondentId = window.formRendererData?.respondentId;
  const flattenPages = window.formRendererData?.flattenPages === "true";
  const passThroughData = window.formRendererData?.passThroughData;
  const apiUrl = window.formRendererData?.formCallbackUrl;
  const apiKey = window.formRendererData?.apiKey;

  const { isLoading, error, data } = useQuery({
    queryKey: ["formData"],
    queryFn: () =>
      fetch(`${apiUrl}form/${formId}`, {
        headers: {
          "x-api-key": apiKey,
        },
      }).then((res) => res.json()),
  });

  if (!formId) {
    return (
      <Alert
        title="No quiz found"
        message="The required Form ID was not found, this form cannot be rendered"
        type="error"
      />
    );
  }

  if (isLoading) {
    return (
      <div className="fr-flex fr-flex-col fr-w-full fr-h-screen fr-items-center fr-justify-center">
        <PulseLoader size={30} color="#36d7b7" />
        <p>Loading ...</p>
      </div>
    );
  }

  if (!respondentId) {
    return (
      <div className="fr-flex fr-flex-col fr-w-full fr-h-screen fr-items-center fr-justify-center">
        <Alert
          title="Fatal error:"
          message="The required respondent ID was not found, this form cannot be rendered."
          type="error"
        />
      </div>
    );
  }

  if (error) {
    console.error(
      `Server error fetching quiz from server for respondentId: ${respondentId}, formId: ${formId}, message (if any): ${JSON.stringify(error)}`,
    );
    return (
      <div className="fr-flex fr-flex-col fr-w-full fr-h-screen fr-items-center fr-justify-center">
        <Alert
          title="Fatal error:"
          message="The form could not be loaded. Something could be wrong with the server."
          type="error"
        />
      </div>
    );
  }

  return (
    <ErrorBoundary fallback={<ErrorFallback />} onError={logError}>
      <Form
        {...data.form}
        formId={formId}
        respondentId={respondentId}
        passThroughData={passThroughData}
        flattenPages={flattenPages}
      />
    </ErrorBoundary>
  );
}
export default App;
